import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ProtectedImage from "../../components/ProtectedImage";
import { IMAGE_URL } from "../../services/api";
import FormDrawer from "../../components/layout/FormDrawer";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";

const DRAWER_WIDTH = { xs: "100%", sm: 560, md: 720 };

const DetailItem = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BRAND.border}`,
      bgcolor: "#fff",
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: BRAND.orangeLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: BRAND.orange,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: BRAND.muted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: value ? BRAND.text : BRAND.muted,
          fontWeight: value ? 500 : 400,
          mt: 0.25,
          wordBreak: "break-word",
        }}
      >
        {value || "Not provided"}
      </Typography>
    </Box>
  </Box>
);

const PermissionBadge = ({ checked, label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      bgcolor: checked ? BRAND.orangeLight : BRAND.bg,
      color: checked ? BRAND.orange : BRAND.muted,
      fontWeight: 600,
      fontSize: "0.7rem",
      border: `1px solid ${checked ? BRAND.orange : BRAND.border}`,
    }}
  />
);

const EmployerViewModal = ({ show, handleClose, employer }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!employer) return null;

  const fullName = `${employer.first_name || ""} ${employer.last_name || ""}`.trim();
  const initials = `${employer.first_name?.[0] || ""}${employer.last_name?.[0] || ""}`.toUpperCase();
  const photoPath = employer.photo ? `${IMAGE_URL}${employer.photo}` : null;

  return (
    <FormDrawer
      open={show}
      onClose={handleClose}
      width={DRAWER_WIDTH}
      title={fullName || "Employer"}
      subtitle="Employer profile and permissions"
      footer={
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          sx={{ ...primaryButtonSx, px: 4 }}
        >
          Close
        </Button>
      }
    >
      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Basic Information" />
        <Tab label="Permissions" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              mb: 3,
              borderRadius: "16px",
              background: `linear-gradient(135deg, ${BRAND.navy} 0%, #243b5e 60%, rgba(255,106,0,0.25) 100%)`,
            }}
          >
            {photoPath ? (
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "3px solid rgba(255,255,255,0.3)",
                }}
              >
                <ProtectedImage
                  path={photoPath}
                  alt={fullName}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            ) : (
              <Avatar
                sx={{
                  width: 88,
                  height: 88,
                  mx: "auto",
                  mb: 2,
                  bgcolor: BRAND.orange,
                  fontSize: "1.75rem",
                  fontWeight: 700,
                }}
              >
                {initials || "?"}
              </Avatar>
            )}

            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              {fullName}
            </Typography>
            <Chip
              label={employer.is_active ? "Active" : "Inactive"}
              size="small"
              sx={{
                mt: 1,
                bgcolor: employer.is_active
                  ? "rgba(74,222,128,0.2)"
                  : "rgba(255,255,255,0.15)",
                color: employer.is_active ? "#4ADE80" : "rgba(255,255,255,0.8)",
                fontWeight: 600,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <DetailItem
              icon={<EmailOutlinedIcon fontSize="small" />}
              label="Email"
              value={employer.email}
            />
            <DetailItem
              icon={<PhoneOutlinedIcon fontSize="small" />}
              label="Phone"
              value={employer.mobile_number}
            />
            <DetailItem
              icon={<BusinessOutlinedIcon fontSize="small" />}
              label="Company"
              value={employer.company?.company_name}
            />
            <DetailItem
              icon={<LocationOnOutlinedIcon fontSize="small" />}
              label="Location"
              value={employer.company?.location}
            />
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {(employer.permissions?.permissions || []).length === 0 ? (
            <Typography sx={{ color: BRAND.muted, textAlign: "center", py: 4 }}>
              No permissions configured
            </Typography>
          ) : (
            employer.permissions.permissions.map((perm) => (
              <Box
                key={perm.module}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: `1px solid ${BRAND.border}`,
                  bgcolor: perm.enabled ? "#fff" : BRAND.bg,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: BRAND.navy }}
                  >
                    {perm.moduleLabel || perm.module}
                  </Typography>
                  <Chip
                    label={perm.enabled ? "Enabled" : "Disabled"}
                    size="small"
                    sx={{
                      bgcolor: perm.enabled ? BRAND.orangeLight : BRAND.bg,
                      color: perm.enabled ? BRAND.orange : BRAND.muted,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {[
                    "view",
                    "edit",
                    "create",
                    "delete",
                    "approve",
                    "reject",
                    "export",
                    "import",
                  ].map(
                    (key) =>
                      perm[key] !== undefined && (
                        <PermissionBadge
                          key={key}
                          checked={Boolean(perm[key])}
                          label={key.replace("_", " ")}
                        />
                      )
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </FormDrawer>
  );
};

export default EmployerViewModal;
